/**
 * DOM Module
 *
 * Element-resolution helpers. Distinct from ui.ts, whose functions operate on
 * already-resolved elements (widget-state mutation).
 */

/**
 * Looks up a required element by id, returning it typed as `T`.
 *
 * The app's DOM lookups assume the element exists (they were previously written
 * as unguarded `as` casts). This helper preserves that contract but fails fast
 * with an identifying error instead of surfacing a missing element as a later,
 * opaque null dereference.
 *
 * Use only for elements that MUST exist. For genuinely optional elements, use a
 * guarded `querySelector`/`getElementById` with an explicit null check instead.
 *
 * @param id - The element id to look up.
 * @returns The element, typed as `T`.
 * @throws {Error} If no element with that id exists in the document.
 */
export function getRequiredElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (element === null) {
    throw new Error(`Required element not found: #${id}`);
  }
  return element as T;
}
